import { Form, FormProps, Input, Modal, ModalProps } from 'antd'
import isEqual from 'lodash.isequal'
import { useCallback, useEffect, useState } from 'react'
import { IBank } from '../../../interfaces/banks'
import { ButtonListFooterWithDeleteModalSM } from '../../../pages/components/buttonsList'
import {
  confirmModalDeleteProps,
  confirmModalUnsavedChangesProps,
} from '../../../pages/components/constants'
import {
  bankAddressRules,
  bankNameRules,
  bankSwiftRules,
  intermediaryAddressRules,
  intermediaryBankNameRules,
  intermediaryBankTelRules,
  intermediarySwiftRules,
} from '../../../pages/payment-request/addBank.constants'
import { companyTinVatUidRules as bankTinVatUidRules } from '../../../pages/payment-request/addCompany.constants'

import { inputSimpleStyle } from '../../../pages/styles/style'
import { clearFieldErrorMessageOnChange } from '../../../utils/form.constants'
import { useBank } from '../../../utils/hooks/useBank.hook'
import { useDeleteBank } from '../../../utils/hooks/useDeleteBank.hook'
import { useNotificationOkCancel } from '../../../utils/hooks/useNotificationOkCancel.hook'
import { useUpdateBank } from '../../../utils/hooks/useUpdateBank.hook'
import {
  AutoCompleteCountry,
  Option,
} from '../../../components/inputs/autocomplete/autocompleteCountry'
import { mapCountry } from '../../../utils/edit.constants'
import { DownOutlined } from '@ant-design/icons'

interface IProps {
  formProps: FormProps
  modalProps?: ModalProps
  closeModal: () => void
  editingId?: number
  changeEditingId: (id: number | undefined) => void
  refetchList: () => void
  show: () => void
  isViewOnly?: boolean
  userId?: number | null
  isCustomer?: boolean
}

export const EditBank = ({
  modalProps,
  formProps,
  editingId,
  changeEditingId,
  closeModal,
  refetchList,
  show,
  isViewOnly = false,
  userId,
  isCustomer = false,
}: IProps) => {
  const { data: loadData, isLoading: loadIsLoading } = useBank(editingId)
  const { update, isLoading: updateIsLoading } = useUpdateBank(editingId)
  const [swiftFieldValue, setSwiftFieldValue] = useState<string>()
  const [isFieldValidationSimple, setIsFieldValidationSimple] =
    useState<boolean>(false)
  const [loadedFormValues, setLoadedFormValues] = useState({})
  const [countryData, setCountryData] = useState<any>()

  const { deleteBank } = useDeleteBank()

  const onOkDelete = useCallback(async () => {
    if (typeof editingId === 'number') {
      changeEditingId(undefined)
      await deleteBank(editingId)
      closeModal()
      refetchList()
    }
  }, [changeEditingId, closeModal, deleteBank, editingId, refetchList])

  const { openNotification, contextHolder: deleteContextHolder } =
    useNotificationOkCancel({
      ...confirmModalDeleteProps,
      onOk: onOkDelete,
    })

  const formIsLoading = loadIsLoading || updateIsLoading

  const clearEditingId = useCallback(() => {
    changeEditingId(undefined)
  }, [changeEditingId])

  useEffect(() => {
    if (loadData) {
      const values: IBank = {
        name: loadData.name,
        swift: loadData.swift,
        legalAddress: loadData.legalAddress,
        country: loadData.country,
      }

      if (isCustomer) {
        values.country = loadData.country
      } else {
        values.tin = loadData.tin
        values.phoneNumber1 = loadData.phoneNumber1
        values.intermediaryBank = loadData.intermediaryBank
        values.intermediarySwift = loadData.intermediarySwift
        values.intermediaryAddress = loadData.intermediaryAddress
      }

      formProps?.form?.setFieldsValue(values)
      setSwiftFieldValue(loadData.swift)
      setLoadedFormValues(formProps?.form?.getFieldsValue())

      // Initialize countryData if country exists
      if (loadData.country) {
        setCountryData({
          value: loadData.country,
          label: loadData.country,
        })
      }
    } else {
      closeModal()
    }
  }, [loadData, formProps?.form, closeModal, isViewOnly, isCustomer])

  useEffect(() => {
    if (editingId !== undefined) {
      show()
    } else {
      closeModal()
    }
  }, [editingId, closeModal, show])

  const onUnsavedChangesOk = useCallback(() => {
    formProps?.form?.resetFields()
    clearEditingId()
  }, [formProps.form, clearEditingId])

  const onSubmit = useCallback(
    async (formValues: IBank) => {
      const body: any = {
        name: formValues.name,
        swift: formValues.swift,
        legalAddress: formValues.legalAddress,
        tin: formValues.tin?.length ? formValues.tin : null,
        country: formValues.country || null,
        userId: userId || loadData?.userId || null,
      }
      if (isCustomer) {
        body.country = formValues.country
      } else {
        body.phoneNumber1 = formValues.phoneNumber1
        body.intermediaryBank = formValues.intermediaryBank
        body.intermediarySwift = formValues.intermediarySwift
        body.intermediaryAddress = formValues.intermediaryAddress
      }
      const isSuccess = await update(body)

      if (isSuccess) {
        refetchList()
        onUnsavedChangesOk()
        closeModal()
      }
    },

    [refetchList, update, closeModal, onUnsavedChangesOk, userId, isCustomer, loadData?.userId]
  )

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onUnsavedChangesOk,
  })

  const onModalCancel = useCallback(() => {
    const isFormChanged = formProps
      ? !isEqual(formProps.form?.getFieldsValue(), loadedFormValues)
      : false
    if (isFormChanged) {
      openUnsavedChangesNotification()
    } else {
      onUnsavedChangesOk()
    }
  }, [
    formProps,
    loadedFormValues,
    openUnsavedChangesNotification,
    onUnsavedChangesOk,
  ])

  const onSave = useCallback(() => {
    const fieldValue = formProps.form?.getFieldValue('swift')
    if (swiftFieldValue === fieldValue) {
      setIsFieldValidationSimple(true)
    }

    formProps.form?.submit()
  }, [formProps.form, swiftFieldValue])

  const Title = useCallback(
    () =>
      isViewOnly ? (
        <>
          <p style={{ marginBottom: '0px' }}>Bank information</p>
          <p>#{editingId}</p>
        </>
      ) : (
        <>
          <p style={{ marginBottom: '0px' }}>Edit bank information</p>
          <p>#{editingId}</p>
        </>
      ),
    [editingId, isViewOnly]
  )

  const changeCountryData = useCallback(
    (data: any) => {
      if (data.id !== countryData?.id) {
        setCountryData(data)
      }
    },
    [countryData?.id]
  )

  const onSelectCountry = useCallback(
    (_: any, { data }: Option<any>) => {
      return changeCountryData(data)
    },
    [changeCountryData]
  )

  const clearCountryData = useCallback(() => {
    setCountryData(undefined)
  }, [])

  if (editingId === undefined) {
    return null
  }

  return (
    <>
      <Modal
        {...modalProps}
        title={<Title />}
        className='smartModalTitle'
        onCancel={onModalCancel}
        footer={
          isViewOnly
            ? null
            : ButtonListFooterWithDeleteModalSM(
              onSave,
              onModalCancel,
              openNotification,
              formIsLoading
            )
        }>
        {unsavedChangesContextHolder}
        {deleteContextHolder}
        <Form
          {...formProps}
          layout='vertical'
          onFinish={onSubmit}
          onValuesChange={clearFieldErrorMessageOnChange(formProps?.form)}>
          <Form.Item name='name' rules={bankNameRules}>
            <Input
              placeholder='Bank name'
              size='large'
              style={inputSimpleStyle}
              disabled={isViewOnly}
            />
          </Form.Item>
          <Form.Item
            name='swift'
            rules={
              isFieldValidationSimple ? intermediarySwiftRules : bankSwiftRules
            }>
            <Input
              placeholder='SWIFT'
              size='large'
              style={inputSimpleStyle}
              disabled={isViewOnly}
            />
          </Form.Item>
          <Form.Item name='legalAddress' rules={bankAddressRules}>
            <Input
              placeholder='Bank address: City/Country'
              size='large'
              style={inputSimpleStyle}
              disabled={isViewOnly}
            />
          </Form.Item>
          {isCustomer ? (
            <Form.Item
              name='country'
              rules={[
                { required: true, message: 'Please enter bank country' },
              ]}>
              <AutoCompleteCountry
                resource='api/v1/worldCheck/countries'
                mapItem={mapCountry}
                onSelect={onSelectCountry}
                allowClear
                refetchOnEmptyValue
                onClear={clearCountryData}>
                <Input
                  // style={{ fontSize: 16 }}
                  placeholder='Country of incorporation'
                  suffix={<DownOutlined />}
                />
              </AutoCompleteCountry>
            </Form.Item>
          ) : (
            <>
              <Form.Item name='tin' rules={bankTinVatUidRules}>
                <Input
                  placeholder='TIN / RegNo'
                  size='large'
                  style={inputSimpleStyle}
                  disabled={isViewOnly}
                />
              </Form.Item>
              <Form.Item name='phoneNumber1' rules={intermediaryBankTelRules}>
                <Input
                  placeholder='Tel'
                  size='large'
                  style={inputSimpleStyle}
                  disabled={isViewOnly}
                />
              </Form.Item>
              <Form.Item
                name='intermediaryBank'
                rules={intermediaryBankNameRules}>
                <Input
                  placeholder='intermediary Bank name'
                  size='large'
                  style={inputSimpleStyle}
                  disabled={isViewOnly}
                />
              </Form.Item>
              <Form.Item
                name='intermediarySwift'
                rules={intermediarySwiftRules}>
                <Input
                  placeholder='Intermediary SWIFT'
                  size='large'
                  style={inputSimpleStyle}
                  disabled={isViewOnly}
                />
              </Form.Item>
              <Form.Item
                name='intermediaryAddress'
                rules={intermediaryAddressRules}>
                <Input
                  placeholder='Intermediary: City/Country'
                  size='large'
                  style={inputSimpleStyle}
                  disabled={isViewOnly}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  )
}
